import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { services } from '@/mocks/services';

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://biffdigital.com';

const serviceSlugs: Record<string, string> = {
  'Marka Konumlandırması': 'marka-konumlandirmasi',
  'Dijital Pazarlama ve Reklam': 'dijital-pazarlama-ve-reklam',
  'Web Sitesi ve Yazılım': 'web-sitesi-ve-yazilim',
  'E-Ticaret ve E-İhracat': 'e-ticaret-ve-e-ihracat',
  'B2B Lead Generation': 'b2b-lead-generation',
  'İçerik Üretimi ve Sosyal Medya': 'icerik-uretimi-ve-sosyal-medya',
};

const serviceFAQItems = [
  {
    question: 'Marka konumlandırması hizmeti nasıl çalışır?',
    answer: 'Marka konumlandırması sürecimiz pazar analizi, hedef kitle segmentasyonu ve rakip analiziyle başlar. Benzersiz değer teklifinizi belirler, marka kişiliğinizi tanımlar ve bunu tüm dijital kanallarda tutarlı şekilde konumlandırırız. 5 yıldır 6+ sektörde uyguladığımız bu metodolojiyle markaların sektörde fark edilir hale gelmesini sağlıyoruz.',
  },
  {
    question: 'Dijital reklam kampanyalarında nasıl ölçümleme yapıyorsunuz?',
    answer: 'Google Ads ve Meta Ads kampanyalarımızı günlük KPI takibi, haftalık performans raporları ve aylık stratejik değerlendirme döngüsüyle yönetiyoruz. ROAS, CPA, CTR ve dönüşüm oranı gibi metrikleri sürekli optimize ediyoruz. Tüm raporlarımız şeffaftır, müşterilerimiz gerçek zamanlı dashboard\'larla kampanyalarını 7/24 izleyebilir.',
  },
  {
    question: 'Web sitesi yaptırırken nelere dikkat etmeliyim?',
    answer: 'Modern bir web sitesi hız (Core Web Vitals), mobil uyumluluk, SEO altyapısı, güvenlik (SSL) ve dönüşüm odaklı UX tasarımını bir arada sunmalıdır. BIFF Digital olarak tüm web projelerimizi performans odaklı geliştiriyor, sayfa hızını 90+ Google PageSpeed skoruna ulaştıracak şekilde optimize ediyoruz.',
  },
  {
    question: 'E-ticarette hangi platformları kullanıyorsunuz?',
    answer: 'Trendyol, Hepsiburada, Amazon, Shopify ve WooCommerce ekosistemlerinde tam hizmet veriyoruz. Pazar yeri entegrasyonu, ürün optimizasyonu, reklam yönetimi ve lojistik danışmanlığıyla e-ticaret operasyonunuzu baştan sona yönetiyoruz. E-ihracat tarafında Amazon Global ve yerel pazar yerleriyle markanızı uluslararası pazarlara açıyoruz.',
  },
  {
    question: 'B2B lead generation kaç ayda sonuç verir?',
    answer: 'B2B lead generation kampanyalarımız genelde ilk 30 günde ilk lead\'leri üretmeye başlar. 90 gün içinde optimize edilmiş bir lead akışı elde edilir. LinkedIn Sales Navigator ve e-posta otomasyon araçlarıyla yürüttüğümüz outreach kampanyalarında, hedef sektöre ve ICP\'ye bağlı olarak aylık 50-200+ kaliteli lead elde etmek mümkündür.',
  },
  {
    question: 'Sosyal medya içerik üretimi için nasıl bir strateji izliyorsunuz?',
    answer: 'İçerik stratejimiz markanızın hedef kitlesine ve platform dinamiklerine özel olarak şekillenir. Profesyonel video prodüksiyonu, fotoğraf çekimi, grafik tasarım ve copywriting hizmetlerini tek çatı altında sunuyoruz. Haftalık içerik takvimi oluşturur, her postu A/B testlerle optimize eder ve aylık performans raporlarıyla stratejiyi sürekli iyileştiririz.',
  },
];

const serviceSchemas = services.map((s) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: s.title,
  provider: {
    '@type': 'Organization',
    name: 'BIFF Digital',
    url: siteUrl,
    telephone: '+905557101968',
  },
  description: s.description,
  areaServed: { '@type': 'Country', name: 'Turkey' },
  serviceType: s.title,
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: `${s.title} Hizmet Detayları`,
    itemListElement: [{
      '@type': 'Offer',
      itemOffered: { '@type': 'Service', name: s.title, description: s.description },
    }],
  },
}));

const mergedFAQSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  name: 'BIFF Digital Hizmetlerimiz Hakkında Sık Sorulanlar',
  mainEntity: serviceFAQItems.map((q) => ({
    '@type': 'Question',
    name: q.question,
    acceptedAnswer: { '@type': 'Answer', text: q.answer },
  })),
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: siteUrl },
    { '@type': 'ListItem', position: 2, name: 'Hizmetlerimiz', item: `${siteUrl}/hizmetler` },
  ],
};

const allSchemas = {
  '@context': 'https://schema.org',
  '@graph': [
    ...serviceSchemas,
    mergedFAQSchema,
    breadcrumbSchema,
  ],
};

export default function Hizmetler() {
  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(allSchemas) }}
      />
      <Navbar />
      <main className="pt-16 md:pt-20">
        {/* Header */}
        <section className="section-padding bg-background-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-12 md:mb-16">
              <p className="text-primary-500 font-bold text-sm tracking-widest uppercase mb-2">
                SANA NE LAZIM?
              </p>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-50 tracking-tighter mb-4">
                İŞİMİZ BU
              </h1>
              <p className="text-sm md:text-base text-foreground-400 font-medium max-w-3xl speakable-summary">
                BIFF Digital olarak marka konumlandırması, dijital reklam yönetimi, web ve yazılım geliştirme, e-ticaret danışmanlığı, B2B lead generation ve içerik üretimi olmak üzere altı ana hizmet alanında, Ankara, İstanbul ve İzmir&apos;deki müşterilerimize 5 yıldır veri odaklı ve ölçülebilir sonuçlar sunuyoruz.
              </p>
            </div>

            {/* Services Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {services.map((service, idx) => (
                <div
                  key={service.id}
                  id={serviceSlugs[service.title]}
                  className="glass-card-hover rounded-2xl p-6 md:p-8 group"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <div className="w-14 h-14 bg-background-200 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary-500/10 transition-colors duration-300">
                    <i className={`${service.icon} text-2xl text-primary-500`} />
                  </div>
                  <h2 className="text-lg md:text-xl font-bold text-foreground-50 mb-3 group-hover:text-primary-500 transition-colors">
                    {service.title}
                  </h2>
                  <p className="text-sm md:text-base text-foreground-400 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Service FAQ Section */}
        <section className="section-padding bg-background-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 md:mb-14">
              <p className="text-primary-500 font-bold text-sm tracking-widest uppercase mb-2">
                DETAYLAR
              </p>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-50 tracking-tighter">
                HİZMETLERİMİZ HAKKINDA
              </h2>
              <p className="text-sm md:text-base text-foreground-400 mt-3 font-medium">
                Her hizmet alanımızla ilgili en sık sorulan soruları, veriye dayalı ve somut cevaplarla burada topladık.
              </p>
            </div>

            <div className="space-y-8">
              {serviceFAQItems.map((item, idx) => (
                <div key={idx} className="glass-card rounded-2xl p-6 md:p-8">
                  <h3 className="text-base md:text-lg font-bold text-foreground-100 mb-3">
                    {item.question}
                  </h3>
                  <p className="text-sm md:text-base text-foreground-400 leading-relaxed">
                    {item.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section-padding bg-background-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="glass-card rounded-2xl p-6 md:p-10 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-50 mb-3">
                Projeni Hayata Geçirelim
              </h2>
              <p className="text-sm md:text-base text-foreground-400 mb-8 max-w-xl mx-auto">
                Hangi hizmetimiz hakkında daha detaylı bilgi almak istiyorsun? Uzman ekibimizle görüş, sana özel çözümleri konuşalım.
              </p>
              <a href="/#contact" className="btn-primary">
                Ücretsiz Danışmanlık
                <i className="ri-arrow-right-line" />
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}